import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { authFileClick } from "@/lib/openFile";
import ClientShell from "@/components/ClientShell";
import AuthImage from "@/components/AuthImage";
import GuestGate from "@/components/GuestGate";
import { useAuth } from "@/context/AuthContext";
import { useCategories } from "@/hooks/useCategories";
import { ArrowLeft, ChatCircleText, Package, FilePdf, Minus, Plus, Clock } from "@phosphor-icons/react";
import ShareLinkButton from "@/components/ShareLinkButton";
import { specsUrl } from "@/lib/publicUrl";
import ProductHighlights from "@/components/ProductHighlights";
import ComparisonScorecard from "@/components/ComparisonScorecard";
import CompetitorTable from "@/components/CompetitorTable";
import FactoryVideoGallery, { youtubeId } from "@/components/FactoryVideoGallery";

export { youtubeId };

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [material, setMaterial] = useState("");
  const [size, setSize] = useState("");
  const [finish, setFinish] = useState("");
  const [qty, setQty] = useState(1);
  const { isGuest } = useAuth();
  const { byId, labelFor } = useCategories(isGuest);

  const fetchProduct = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(isGuest ? `/public/product/${productId}` : `/products/${productId}`);
      setProduct(res.data);
      setMaterial(res.data.materials?.[0] || "");
      setSize(res.data.option_sizes?.[0] || "");
      setFinish(res.data.finishes?.[0]?.name || "");
      setActiveImage(0);
    } catch (e) {
      console.error("[ProductDetail] failed:", e);
    }
    setLoading(false);
  }, [productId, isGuest]);

  useEffect(() => { fetchProduct(); }, [fetchProduct]);

  useEffect(() => {
    let alive = true;
    api
      .get(isGuest ? `/public/product/${productId}/comparison` : `/products/${productId}/comparison`)
      .then((res) => { if (alive) setComparison(res.data); })
      .catch(() => { if (alive) setComparison(null); });
    return () => { alive = false; };
  }, [productId, isGuest]);

  const images = useMemo(() => {
    if (!product) return [];
    const gallery = (product.gallery || [])
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((g) => g.image_path);
    return [product.image_path, ...gallery].filter(Boolean);
  }, [product]);

  if (loading) {
    return <ClientShell><div className="h-96 bg-white border border-zinc-200 animate-pulse" /></ClientShell>;
  }
  if (!product) {
    return (
      <ClientShell>
        <div className="bg-white border border-zinc-200 p-12 text-center">
          <div className="text-zinc-500">Product not found</div>
          <button onClick={() => navigate(-1)} className="mt-4 underline">Go back</button>
        </div>
      </ClientShell>
    );
  }

  const category = byId(product.category);
  const definedVideos = (product.videos || []).filter((v) => youtubeId(v.url));
  const videos = definedVideos.length
    ? definedVideos.slice(0, 5)
    : product.video_url && youtubeId(product.video_url)
    ? [{ title: "Factory tour", url: product.video_url }]
    : [];
  const docs = (product.documents || []).filter((d) => d.pdf_path);

  const quoteHref =
    `/quotations/new?category=${product.category}&product=${encodeURIComponent(product.name)}` +
    (product.brand ? `&brand=${encodeURIComponent(product.brand)}` : "") +
    (material ? `&sel_material=${encodeURIComponent(material)}` : "") +
    (size ? `&sel_size=${encodeURIComponent(size)}` : "") +
    (finish ? `&sel_finish=${encodeURIComponent(finish)}` : "") +
    `&qty=${qty}`;

  return (
    <ClientShell>
      <Link
        to={`/category/${product.category}`}
        data-testid="product-back-link"
        className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 transition-colors mb-6"
      >
        <ArrowLeft size={14} /> Back to {category?.label || "category"}
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">
        {/* Gallery */}
        <div className="flex gap-3 md:gap-4" data-testid="product-gallery">
          {images.length > 1 && (
            <div className="flex flex-col gap-3 w-16 md:w-20 shrink-0 max-h-[520px] overflow-y-auto pr-0.5">
              {images.map((path, i) => (
                <button
                  key={path}
                  onClick={() => setActiveImage(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`relative aspect-square overflow-hidden border transition-colors ${
                    i === activeImage ? "border-zinc-900" : "border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  <AuthImage
                    path={path}
                    alt={`${product.name} view ${i + 1}`}
                    guest={isGuest}
                    className="absolute inset-0 w-full h-full object-cover"
                    fallback={<div className="absolute inset-0 bg-zinc-100" />}
                  />
                </button>
              ))}
            </div>
          )}
          <div className={`relative flex-1 ${category?.blob || "bg-zinc-100"} aspect-square overflow-hidden`}>
            <AuthImage
              path={images[activeImage] || product.image_path}
              alt={product.name}
              guest={isGuest}
              className="absolute inset-0 w-full h-full object-cover"
              fallback={<div className="absolute inset-0 grid place-items-center"><Package size={64} className={category?.accent_fg} weight="duotone" /></div>}
            />
          </div>
        </div>

        {/* Details */}
        <div className="flex flex-col">
          <div className="mt-tiny text-[#8B4513] uppercase tracking-[0.18em] text-xs">
            {product.brand || labelFor(product.category)}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-zinc-900 mt-2 tracking-tight">
            {product.name}
          </h1>
          {product.description && (
            <p className="mt-4 text-zinc-700 leading-relaxed text-sm md:text-base">{product.description}</p>
          )}

          <ProductHighlights highlights={product.highlights} />

          {(product.materials || []).length > 0 && (
            <div className="mt-7" data-testid="product-material-pills">
              <div className="text-sm text-zinc-500 mb-2">Material</div>
              <div className="flex flex-wrap gap-2">
                {product.materials.map((m) => (
                  <button
                    key={m}
                    onClick={() => setMaterial(m)}
                    data-testid={`material-pill-${m}`}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-colors ${
                      material === m
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-800 border-zinc-300 hover:border-zinc-900"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(product.option_sizes || []).length > 0 && (
            <div className="mt-6" data-testid="product-size-pills">
              <div className="text-sm text-zinc-500 mb-2">Size</div>
              <div className="flex flex-wrap gap-2">
                {product.option_sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`size-pill-${s}`}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-colors ${
                      size === s
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-800 border-zinc-300 hover:border-zinc-900"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(product.finishes || []).length > 0 && (
            <div className="mt-6" data-testid="product-finish-swatches">
              <div className="text-sm text-zinc-500 mb-2">
                Finish: <span className="text-zinc-900 font-medium">{finish || "—"}</span>
              </div>
              <div className="flex flex-wrap gap-3">
                {product.finishes.map((f) => (
                  <button
                    key={f.name}
                    title={f.name}
                    onClick={() => setFinish(f.name)}
                    data-testid={`finish-swatch-${f.name}`}
                    className={`w-9 h-9 rounded-full transition-all ${
                      finish === f.name
                        ? "ring-1 ring-offset-2 ring-zinc-900"
                        : "ring-1 ring-offset-2 ring-transparent hover:ring-zinc-300"
                    }`}
                    style={{ backgroundColor: f.hex || "#d4d4d8" }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mt-7">
            <div className="text-sm text-zinc-500 mb-2">Quantity</div>
            <div className="inline-flex items-center border border-zinc-300 rounded-full overflow-hidden" data-testid="product-quantity-selector">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="qty-decrease"
                className="px-4 py-3 hover:bg-zinc-100 transition-colors"
              >
                <Minus size={16} />
              </button>
              <input
                data-testid="qty-input"
                value={qty}
                onChange={(e) => {
                  const n = parseInt(e.target.value.replace(/\D/g, ""), 10);
                  setQty(Number.isNaN(n) || n < 1 ? 1 : n);
                }}
                className="w-16 text-center outline-none text-zinc-900 font-medium bg-transparent"
              />
              <button
                onClick={() => setQty((q) => q + 1)}
                data-testid="qty-increase"
                className="px-4 py-3 hover:bg-zinc-100 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-sm text-zinc-700" data-testid="product-availability">
            <Clock size={18} className="text-[#0047AB]" weight="fill" />
            <span>Made to order — pre-order only{product.lead_time ? ` · lead time ${product.lead_time}` : ""}</span>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            {isGuest ? (
              <button
                onClick={() => navigate("/")}
                data-testid="product-request-quote-button"
                className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04814] text-white px-6 py-3.5 font-medium transition-colors"
              >
                <ChatCircleText size={20} weight="fill" /> Sign in to request a quote
              </button>
            ) : (
              <Link
                to={quoteHref}
                data-testid="product-request-quote-button"
                className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04814] text-white px-6 py-3.5 font-medium transition-colors"
              >
                <ChatCircleText size={20} weight="fill" /> Request quote for this material
              </Link>
            )}
            <ShareLinkButton
              url={specsUrl(product.product_id)}
              idleLabel="Share public specs"
              copiedLabel="Specs link copied"
              toastLabel="Public specs link copied"
              testId={`share-specs-${product.product_id}`}
              className="py-3.5"
            />
          </div>

          {docs.length > 0 && (
            <div className="mt-10">
              <div className="mt-tiny text-zinc-500 mb-4">Product documents</div>
              {isGuest ? (
                <GuestGate
                  label="Sign in to view documents"
                  note="Technical data sheets & certificates are available to signed-in members."
                  testId="guest-gate-docs"
                />
              ) : (
              <div
                className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 md:gap-4"
                data-testid="product-documents-grid"
              >
                {docs
                  .slice()
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((d) => (
                    <a
                      key={d.doc_id}
                      href={`${API}/files/${d.pdf_path}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={authFileClick(d.pdf_path)}
                      data-testid={`product-doc-${d.doc_id}`}
                      className="group flex flex-col items-center gap-2 text-center hover:-translate-y-0.5 transition-transform"
                    >
                      <div className="w-full aspect-square border border-zinc-200 bg-zinc-50 grid place-items-center overflow-hidden group-hover:border-[#0047AB] group-hover:bg-white transition-colors">
                        {d.icon_path ? (
                          <AuthImage
                            path={d.icon_path}
                            alt={d.name}
                            className="w-full h-full object-contain p-2"
                            fallback={<FilePdf size={36} className="text-[#B22222]" weight="duotone" />}
                          />
                        ) : (
                          <FilePdf size={40} className="text-[#B22222]" weight="duotone" />
                        )}
                      </div>
                      <div className="text-xs md:text-sm font-medium text-zinc-800 group-hover:text-[#0047AB] transition-colors line-clamp-2 leading-tight">
                        {d.name}
                      </div>
                    </a>
                  ))}
              </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Factory in Action gallery — locked for guests */}
      {isGuest ? (
        (videos || []).length > 0 && (
          <div className="mt-12">
            <div className="mt-tiny text-zinc-500 mb-1">Factory in Action</div>
            <GuestGate
              label="Sign in to watch factory videos"
              note="See how this material is produced. Videos are available to signed-in members."
              testId="guest-gate-videos"
            />
          </div>
        )
      ) : (
        <FactoryVideoGallery videos={videos} />
      )}

      <ComparisonScorecard
        data={comparison}
        renderImage={(p) => (
          <AuthImage
            path={p.image_path}
            alt={p.name}
            guest={isGuest}
            className="absolute inset-0 w-full h-full object-cover"
            fallback={<div className="absolute inset-0 bg-zinc-100" />}
          />
        )}
      />

      <CompetitorTable
        benchmark={product.benchmark}
        datasheetUrl={isGuest ? () => null : (c) => (c.datasheet_path ? `${API}/files/${c.datasheet_path}` : null)}
        onOpenDatasheet={isGuest ? undefined : (c) => authFileClick(c.datasheet_path)}
      />
    </ClientShell>
  );
}
